import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Loader2 } from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import {
  listProjects,
  getBusinessUnits,
  getDivisions,
  getLocations,
  getProjectLocation,
  createProject,
  updateProject,
  type ProjectListRow,
  type ProjectFormInput,
  type DSRVariant,
} from '@/services/db/adminProjects';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';

const projectSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  code: z
    .string()
    .min(1, 'Code is required')
    .max(32, 'Code must be 32 characters or fewer'),
  businessUnitId: z.string().uuid('Select a business unit'),
  divisionId: z.string().uuid('Select a division'),
  operatorName: z.string().optional(),
  rigName: z.string().optional(),
  wellName: z.string().optional(),
  operationalPhase: z.string().optional(),
  waterDepthFt: z
    .string()
    .optional()
    .refine((v) => !v || !Number.isNaN(Number(v)), 'Must be a number'),
  locationId: z.string().optional(),
  isActive: z.boolean(),
  dsrVariant: z.enum(['lite', 'standard', 'full']),
});

const DSR_VARIANT_OPTIONS: { value: DSRVariant; label: string; description: string }[] = [
  { value: 'lite',     label: 'Lite',     description: 'Minimum data, fastest entry — designed for new crews and light operations.' },
  { value: 'standard', label: 'Standard', description: 'Full daily report (default).' },
  { value: 'full',     label: 'Full',     description: 'Comprehensive report with extended risk tracking.' },
];

type ProjectFormValues = z.infer<typeof projectSchema>;

function toInput(v: ProjectFormValues): ProjectFormInput {
  return {
    name: v.name.trim(),
    code: v.code.trim(),
    division_id: v.divisionId,
    operator_name: v.operatorName?.trim() || null,
    rig_name: v.rigName?.trim() || null,
    well_name: v.wellName?.trim() || null,
    operational_phase: v.operationalPhase?.trim() || null,
    water_depth_ft:
      v.waterDepthFt && v.waterDepthFt.trim() ? Number(v.waterDepthFt) : null,
    is_active: v.isActive,
    dsr_variant: v.dsrVariant,
    location_id: v.locationId || null,
  };
}

export default function ProjectsSection() {
  const { tenantId, user } = useAuthStore();
  const userId = user?.id ?? null;
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<ProjectListRow | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [prevLocationId, setPrevLocationId] = useState<string | null>(null);

  const projectsQuery = useQuery({
    queryKey: ['admin-projects', tenantId],
    queryFn: () => listProjects(tenantId!),
    enabled: !!tenantId,
  });

  const businessUnitsQuery = useQuery({
    queryKey: ['admin-bus', tenantId],
    queryFn: () => getBusinessUnits(tenantId!),
    enabled: !!tenantId,
  });

  const divisionsQuery = useQuery({
    queryKey: ['admin-divisions', tenantId],
    queryFn: () => getDivisions(tenantId!),
    enabled: !!tenantId,
  });

  const locationsQuery = useQuery({
    queryKey: ['admin-locations', tenantId],
    queryFn: () => getLocations(tenantId!),
    enabled: !!tenantId,
  });

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ProjectFormValues>({
    resolver: zodResolver(projectSchema),
    defaultValues: {
      name: '',
      code: '',
      businessUnitId: '',
      divisionId: '',
      operatorName: '',
      rigName: '',
      wellName: '',
      operationalPhase: '',
      waterDepthFt: '',
      locationId: '',
      isActive: true,
      dsrVariant: 'standard',
    },
  });

  const watchedBU = watch('businessUnitId');
  const watchedDivision = watch('divisionId');

  const filteredDivisions = useMemo(() => {
    const all = divisionsQuery.data ?? [];
    if (!watchedBU) return all;
    return all.filter((d) => d.business_unit_id === watchedBU);
  }, [divisionsQuery.data, watchedBU]);

  // Reset division if the selected one doesn't belong to the selected BU
  useEffect(() => {
    if (!watchedDivision) return;
    const match = filteredDivisions.find((d) => d.id === watchedDivision);
    if (!match) setValue('divisionId', '');
  }, [watchedBU, filteredDivisions, watchedDivision, setValue]);

  const openNew = () => {
    setEditing(null);
    setPrevLocationId(null);
    setServerError(null);
    reset({
      name: '',
      code: '',
      businessUnitId: '',
      divisionId: '',
      operatorName: '',
      rigName: '',
      wellName: '',
      operationalPhase: '',
      waterDepthFt: '',
      locationId: '',
      isActive: true,
      dsrVariant: 'standard',
    });
    setSheetOpen(true);
  };

  const openEdit = async (p: ProjectListRow) => {
    setEditing(p);
    setServerError(null);
    // Find the BU via the division FK join
    const division = (divisionsQuery.data ?? []).find(
      (d) => d.id === p.division_id,
    );
    const currentLocationId = await getProjectLocation(p.id).catch(() => null);
    setPrevLocationId(currentLocationId);
    reset({
      name: p.name,
      code: p.code,
      businessUnitId: division?.business_unit_id ?? '',
      divisionId: p.division_id,
      operatorName: p.operator_name ?? '',
      rigName: p.rig_name ?? '',
      wellName: p.well_name ?? '',
      operationalPhase: p.operational_phase ?? '',
      waterDepthFt:
        p.water_depth_ft != null ? String(p.water_depth_ft) : '',
      locationId: currentLocationId ?? '',
      isActive: p.is_active,
      dsrVariant: p.dsr_variant,
    });
    setSheetOpen(true);
  };

  const onSubmit = async (values: ProjectFormValues) => {
    setServerError(null);
    if (!tenantId || !userId) {
      setServerError('Missing session context');
      return;
    }
    try {
      const input = toInput(values);
      if (editing) {
        await updateProject(tenantId, userId, editing.id, input, prevLocationId);
      } else {
        await createProject(tenantId, userId, input);
      }
      queryClient.invalidateQueries({ queryKey: ['admin-projects', tenantId] });
      queryClient.invalidateQueries({
        queryKey: ['admin-invite-projects', tenantId],
      });
      setSheetOpen(false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Save failed';
      // Friendly message for unique-code collision
      if (/duplicate key.*projects.*tenant_id.*code/i.test(msg)) {
        setServerError('A project with this code already exists for this tenant.');
      } else {
        setServerError(msg);
      }
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Projects</h2>
          <p className="text-sm text-muted-foreground">
            Operator, rig, well, and phase metadata. Used in DSR header context
            and reporting.
          </p>
        </div>
        <Button
          onClick={openNew}
          className="bg-mozaic-teal text-white hover:bg-mozaic-teal/90"
        >
          <Plus className="h-4 w-4 mr-1" />
          New Project
        </Button>
      </CardHeader>
      <CardContent>
        {projectsQuery.isLoading ? (
          <div className="flex items-center justify-center py-10 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Project</TableHead>
                <TableHead>Operator</TableHead>
                <TableHead>Rig / Facility</TableHead>
                <TableHead>Well</TableHead>
                <TableHead>Phase</TableHead>
                <TableHead>Variant</TableHead>
                <TableHead className="text-right">Active SSRs</TableHead>
                <TableHead>Status</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {projectsQuery.data && projectsQuery.data.length > 0 ? (
                projectsQuery.data.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className="font-medium">
                      <div>{p.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {p.code}
                        {p.business_unit_name
                          ? ` — ${p.business_unit_name}`
                          : ''}
                        {p.division_name ? ` / ${p.division_name}` : ''}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">
                      {p.operator_name || <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell className="text-sm">
                      {p.rig_name || <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell className="text-sm">
                      {p.well_name || <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell className="text-sm">
                      {p.operational_phase || <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={
                          p.dsr_variant === 'lite'
                            ? 'text-xs border-mozaic-teal text-mozaic-teal'
                            : p.dsr_variant === 'full'
                              ? 'text-xs border-mozaic-navy text-mozaic-navy'
                              : 'text-xs'
                        }
                      >
                        {p.dsr_variant.charAt(0).toUpperCase() + p.dsr_variant.slice(1)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {p.active_ssr_count}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={p.is_active ? 'default' : 'secondary'}
                        className={
                          p.is_active
                            ? 'bg-emerald-100 text-emerald-700 text-xs'
                            : 'text-xs'
                        }
                      >
                        {p.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEdit(p)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={9}
                    className="text-center text-muted-foreground py-8"
                  >
                    No projects yet. Create the first one to begin onboarding a
                    pilot operator.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{editing ? `Edit: ${editing.name}` : 'New Project'}</SheetTitle>
          </SheetHeader>
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="space-y-4 px-4 pb-6"
          >
            {serverError && (
              <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {serverError}
              </div>
            )}

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="p-name">Project Name *</Label>
                <Input id="p-name" {...register('name')} />
                {errors.name && (
                  <p className="text-xs text-destructive">
                    {errors.name.message}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="p-code">Code *</Label>
                <Input
                  id="p-code"
                  placeholder="e.g. BV-001"
                  {...register('code')}
                />
                {errors.code && (
                  <p className="text-xs text-destructive">
                    {errors.code.message}
                  </p>
                )}
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Business Unit *</Label>
                <Select
                  onValueChange={(v) =>
                    setValue('businessUnitId', v ?? '', { shouldValidate: true })
                  }
                  value={watch('businessUnitId') || undefined}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select BU" />
                  </SelectTrigger>
                  <SelectContent>
                    {(businessUnitsQuery.data ?? []).map((b) => (
                      <SelectItem key={b.id} value={b.id}>
                        {b.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.businessUnitId && (
                  <p className="text-xs text-destructive">
                    {errors.businessUnitId.message}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Division *</Label>
                <Select
                  onValueChange={(v) =>
                    setValue('divisionId', v ?? '', { shouldValidate: true })
                  }
                  value={watch('divisionId') || undefined}
                  disabled={!watchedBU || filteredDivisions.length === 0}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={
                        !watchedBU
                          ? 'Select BU first'
                          : filteredDivisions.length === 0
                            ? 'No divisions in this BU'
                            : 'Select division'
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredDivisions.map((d) => (
                      <SelectItem key={d.id} value={d.id}>
                        {d.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.divisionId && (
                  <p className="text-xs text-destructive">
                    {errors.divisionId.message}
                  </p>
                )}
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="p-operator">Operator</Label>
                <Input
                  id="p-operator"
                  placeholder="Occidental Petroleum"
                  {...register('operatorName')}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="p-rig">Rig / Facility</Label>
                <Input
                  id="p-rig"
                  placeholder="Boomvang"
                  {...register('rigName')}
                />
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="p-well">Well Name</Label>
                <Input id="p-well" {...register('wellName')} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="p-phase">Well Phase</Label>
                <Input
                  id="p-phase"
                  placeholder="Production, Drilling, Completion, Other"
                  {...register('operationalPhase')}
                />
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="p-depth">Water Depth (ft)</Label>
                <Input
                  id="p-depth"
                  type="number"
                  inputMode="decimal"
                  placeholder="3500"
                  {...register('waterDepthFt')}
                />
                {errors.waterDepthFt && (
                  <p className="text-xs text-destructive">
                    {errors.waterDepthFt.message}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Location</Label>
                <Select
                  onValueChange={(v) => setValue('locationId', v && v !== '__none__' ? v : '')}
                  value={watch('locationId') || '__none__'}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select location" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— None —</SelectItem>
                    {(locationsQuery.data ?? []).map((l) => (
                      <SelectItem key={l.id} value={l.id}>
                        {l.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>DSR Variant *</Label>
              <Select
                onValueChange={(v) =>
                  setValue('dsrVariant', v as DSRVariant, { shouldValidate: true })
                }
                value={watch('dsrVariant') || 'standard'}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select variant" />
                </SelectTrigger>
                <SelectContent>
                  {DSR_VARIANT_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      <div>
                        <div className="font-medium">{o.label}</div>
                        <div className="text-xs text-muted-foreground">{o.description}</div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Changes apply to new DSRs only. Existing reports keep the variant they were filed under.
              </p>
              {errors.dsrVariant && (
                <p className="text-xs text-destructive">{errors.dsrVariant.message}</p>
              )}
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={watch('isActive')}
                onChange={(e) => setValue('isActive', e.target.checked)}
                className="h-4 w-4 rounded border-input"
              />
              Active
            </label>

            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setSheetOpen(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="bg-mozaic-teal text-white hover:bg-mozaic-teal/90"
              >
                {isSubmitting ? 'Saving…' : editing ? 'Save Changes' : 'Create Project'}
              </Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>
    </Card>
  );
}
